import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../firebase/AuthContext";
import { fetchSubscriptionStatus, openBillingPortal, startCheckout } from "../api/subscription";
import { buildCapabilities, CAPABILITIES } from "./capabilities";
import { fetchCurrentAccess } from "../api/access";
import { shouldBlockEntitlementRefresh, shouldClearEntitlementsForError } from "./refreshPolicy";

const FREE_STATUS = Object.freeze({
  plan: "free", isAdmin: false, isPremium: false, entitled: false,
  status: "inactive", cancelAtPeriodEnd: false,
});

const EntitlementContext = createContext(null);
const GURU_PLAY_CAPABILITY = { TEG: CAPABILITIES.TEG_GURU_PLAYS, MSI: CAPABILITIES.MSI_GURU_PLAYS };
const PLAY_ALERT_CAPABILITY = { TEG: CAPABILITIES.TEG_PLAY_ALERTS, MSI: CAPABILITIES.MSI_PLAY_ALERTS };
const EMPTY_APPLICATIONS = Object.freeze({
  PWG: { enabled: false, accessLevel: "FREE", capabilities: { guruMode: { enabled: false, features: {} } } },
  MSI: { enabled: false, accessLevel: "FULL", features: { guruPlays: { enabled: false }, playAlerts: { enabled: false } } },
  TEG: { enabled: false, accessLevel: "PREMIUM", features: { guruPlays: { enabled: false }, playAlerts: { enabled: false } } },
  "0DTE": { enabled: false, accessLevel: "PREMIUM" },
});

const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const isTransient = (error) => !error?.status || RETRYABLE_STATUSES.has(Number(error.status));
const retryDelay = () => new Promise((resolve) => setTimeout(resolve, 250));

export function accessFailureDisposition(error, { hasLoadedAccess = false } = {}) {
  if (shouldClearEntitlementsForError(error)) return "denied";
  return hasLoadedAccess ? "preserve" : "unavailable";
}

export function profileAccessFingerprint(profile) {
  if (!profile) return "missing";
  return JSON.stringify({
    status: String(profile.status || "").toUpperCase(),
    activationStatus: String(profile.activationStatus || "").toLowerCase(),
    role: String(profile.role || "").toUpperCase(),
    authDisabled: profile.authDisabled === true,
    applications: profile.applications || {},
    legacyFeatures: profile.features || {},
  });
}

export function EntitlementProvider({ children }) {
  const { user, userDoc, authInitialized } = useAuth();
  const [status, setStatus] = useState(FREE_STATUS);
  const [access, setAccess] = useState({ user: null, applications: EMPTY_APPLICATIONS });
  // Start guarded routes in a pending state. Otherwise a Premium user can be
  // redirected to Pricing during the render immediately before the first
  // authenticated status request begins.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accessState, setAccessState] = useState("checking");
  const hasLoadedAccessRef = useRef(false);
  const refreshPromiseRef = useRef(null);
  const refreshGenerationRef = useRef(0);
  const userId = user?.uid || "";
  const accessFingerprint = profileAccessFingerprint(userDoc);

  useEffect(() => {
    refreshGenerationRef.current += 1;
    refreshPromiseRef.current = null;
  }, [userId, accessFingerprint]);

  const refresh = useCallback(async ({ background = false } = {}) => {
    if (!user) {
      setStatus(FREE_STATUS);
      setAccess({ user: null, applications: EMPTY_APPLICATIONS });
      hasLoadedAccessRef.current = false;
      setLoading(false);
      setAccessState("denied");
      return FREE_STATUS;
    }
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const shouldBlock = shouldBlockEntitlementRefresh({ background, hasAccess: hasLoadedAccessRef.current });
    if (shouldBlock) setLoading(true);
    if (!hasLoadedAccessRef.current) setAccessState("checking");
    setError("");
    const generation = ++refreshGenerationRef.current;
    const execute = async () => {
      let attempt = 0;
      while (true) {
        try {
          const [result, accessResult] = await Promise.all([
            fetchSubscriptionStatus(),
            fetchCurrentAccess(),
          ]);
          if (generation !== refreshGenerationRef.current) return result?.subscription || FREE_STATUS;
          const next = result?.subscription || FREE_STATUS;
          setStatus(next);
          setAccess(accessResult);
          hasLoadedAccessRef.current = true;
          const active = accessResult?.user?.status === "ACTIVE";
          const enabled = Object.values(accessResult?.applications || {}).some((application) => application?.enabled === true);
          setAccessState(active && enabled ? "authorized" : "denied");
          return next;
        } catch (err) {
          if (attempt === 0 && isTransient(err)) {
            attempt += 1;
            await retryDelay();
            continue;
          }
          if (generation !== refreshGenerationRef.current) return FREE_STATUS;
          const failureDisposition = accessFailureDisposition(err, {
            hasLoadedAccess: hasLoadedAccessRef.current,
          });
          if (failureDisposition === "denied") {
            // Only an authoritative authentication/authorization response may
            // revoke an access decision that was already established.
            setStatus(FREE_STATUS);
            setAccess({ user: null, applications: EMPTY_APPLICATIONS });
            hasLoadedAccessRef.current = false;
            setAccessState("denied");
          } else if (failureDisposition === "unavailable") {
            // Routing failures, aborts, timeouts and upstream failures are not
            // entitlement decisions. Keep them distinct from Access Pending.
            setAccessState("unavailable");
          }
          setError(err?.message || "Unable to load subscription status");
          throw err;
        } finally {
          if (generation === refreshGenerationRef.current && shouldBlock) setLoading(false);
        }
      }
    };
    const promise = execute().finally(() => {
      if (refreshPromiseRef.current === promise) refreshPromiseRef.current = null;
    });
    refreshPromiseRef.current = promise;
    return promise;
  }, [userId]);

  useEffect(() => {
    if (!authInitialized) return;
    if (!user) {
      setStatus(FREE_STATUS);
      setAccess({ user: null, applications: EMPTY_APPLICATIONS });
      hasLoadedAccessRef.current = false;
      setLoading(false);
      setAccessState("denied");
      return;
    }
    // AuthContext creates/loads the profile asynchronously on first sign-in.
    // Wait for that server-owned profile instead of turning a transient 404
    // into a sticky Free state.
    if (!userDoc) {
      setLoading(true);
      setAccessState("checking");
      return;
    }
    refresh().catch(() => {});
  }, [authInitialized, userId, accessFingerprint, refresh]);

  useEffect(() => {
    if (!user || !access.user || typeof window === "undefined") return undefined;
    let lastRun = 0;
    const revalidate = () => {
      const now = Date.now();
      if (now - lastRun < 15000) return;
      lastRun = now;
      refresh({ background: true }).catch(() => {});
    };
    window.addEventListener("focus", revalidate);
    return () => window.removeEventListener("focus", revalidate);
  }, [user, access.user, refresh]);

  const isAdmin = status.isAdmin === true || String(access.user?.role || "").toUpperCase() === "ADMIN";
  const isPremium = !isAdmin && status.isPremium === true;
  const isStripePremium = isPremium && status.entitlementSource === "stripe";
  const entitled = isAdmin || isPremium;
  const capabilities = useMemo(() => buildCapabilities({
    isAdmin, isPremium, applications: access.applications,
  }), [isAdmin, isPremium, access.applications]);

    const checkout = useCallback(async (productKey = "pwg", term = "monthly") => {
      const result = await startCheckout(productKey, term);

      if (!result?.url) throw new Error("Stripe Checkout URL was not returned.");

      window.location.assign(result.url);
    }, []);

  const manageBilling = useCallback(async () => {
    const result = await openBillingPortal();
    if (!result?.url) throw new Error("Stripe Billing Portal URL was not returned.");
    window.location.assign(result.url);
  }, []);

  const value = useMemo(() => ({
    ...status, ...access, isAdmin, isPremium, isStripePremium, entitled, loading, error, accessState, capabilities,
    can: (capability) => capabilities[capability] === true,
    hasGuruPlaysAccess: (source) => capabilities[GURU_PLAY_CAPABILITY[String(source || "").toUpperCase()]] === true,
    hasPlayAlertsEligibility: (source) => capabilities[PLAY_ALERT_CAPABILITY[String(source || "").toUpperCase()]] === true,
    refresh, checkout, manageBilling,
  }), [status, access, isAdmin, isPremium, isStripePremium, entitled, loading, error, accessState, capabilities, refresh, checkout, manageBilling]);

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlements() {
  const value = useContext(EntitlementContext);
  if (!value) throw new Error("useEntitlements must be used inside EntitlementProvider");
  return value;
}
